import { Modal, Col, Menu, Row, Space, Typography, Dropdown, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import PlatformPermissionsView from "../components/PlatformPermissionsView";
import CompanyPermissionsView from "../components/CompanyPermissionsView";
import PlatformPermissionsFormDialog from "../components/PlatformPermissionsFormDialog";
import CompanyPermissionsFormDialog from "../components/CompanyPermissionFromDialog";
import {
  getPlatformPermissionsExport,
  getCompanyPermissionsExport,
} from "../repository/data.repository";
import { SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import {
  PlatformPermission,
  CompanyPermission,
} from "../repository/data.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function PermissionsPage() {
  const permissions = useSelector(authorizeSelectors.permissions);
  const scope = (sel: string) =>
    sel === "platformRoles" ? "PlatformRolesPermissions" : "CompanyRolesPermissions";
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(
    "platformRoles",
  );
  const [modal, contextHolder] = Modal.useModal();
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [permissionInputMode, setPermissionInputMode] = useState<
    "add" | "update" | "delete"
  >("add");
  const [permissionInputOpen, setPermissionInputOpen] = useState(false);
  const [platformPermissionInputValues, setPlatformpermissionInputValues] =
    useState<PlatformPermission>({} as any);
  const [companyPermissionInputValues, setCompanympermissionInputValues] =
    useState<CompanyPermission>({} as any);

  const items = [
    {
      key: "platformRoles",
      label: "Platform Roles",
    },
    {
      key: "companyRoles",
      label: "Company Roles",
    },
  ];

  // Events

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onSelect(e: { readonly key: string }) {
    setSelectedKey(e.key);
  }

  function onPermissionInputOpen(
    mode: "add" | "update" | "delete",
    values?: PlatformPermission | CompanyPermission,
  ): void {
    setPermissionInputMode(mode);
    setPermissionInputOpen(true);
    switch (selectedKey) {
      case "platformRoles":
        setPlatformpermissionInputValues(
          values
            ? {
                ...values,
                action: {
                  display: values.allowedAction,
                  value: values.allowedAction,
                },
              }
            : ({} as any),
        );
        break;
      case "companyRoles":
        setCompanympermissionInputValues(
          values
            ? {
                ...values,
                action: {
                  display: values.allowedAction,
                  value: values.allowedAction,
                },
              }
            : ({} as any),
        );
    }
  }

  function onPermissionInputFinish(): void {
    setPermissionInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onPermissionInputClose(): void {
    setPermissionInputOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading permissions export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });

    switch (selectedKey) {
      case "platformRoles":
        getPlatformPermissionsExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;
      case "companyRoles":
        getCompanyPermissionsExport({
          searchQuery,
          pageNumber: 0,
          pageSize: Number.MAX_SAFE_INTEGER,
        })
          .then(() => {
            loading.destroy();
          })
          .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
        break;
    }
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Permissions
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={
              !permissions.can(scope(selectedKey ?? "platformRoles"), "create")
            }
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can(
                    scope(selectedKey ?? "platformRoles"),
                    "view",
                  ),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onPermissionInputOpen("add")}
          >
            Add Permissions
          </Dropdown.Button>
        </Space>
      </Row>
      <Row className="flex flex-auto flex-row flex-nowrap">
        <Col className="flex flex-col pb-4">
          <Menu
            className="ml-2 border-none bg-transparent"
            items={items}
            onSelect={onSelect}
            selectedKeys={selectedKey ? [selectedKey] : []}
          />
        </Col>
        <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
          {selectedKey === "platformRoles" && (
            <PlatformPermissionsView
              trigger={triggerView}
              onAction={(key: string, record: PlatformPermission) =>
                onPermissionInputOpen(key as any, record)
              }
              onSearch={onSearch}
            />
          )}

          {selectedKey === "companyRoles" && (
            <CompanyPermissionsView
              trigger={triggerView}
              onAction={(key: string, record: CompanyPermission) =>
                onPermissionInputOpen(key as any, record)
              }
              onSearch={onSearch}
            />
          )}
        </Col>
        {selectedKey === "platformRoles" && (
          <PlatformPermissionsFormDialog
            mode={permissionInputMode}
            open={permissionInputOpen}
            initialValues={platformPermissionInputValues}
            onFinish={onPermissionInputFinish}
            onCancel={onPermissionInputClose}
          />
        )}
        {selectedKey === "companyRoles" && (
          <CompanyPermissionsFormDialog
            mode={permissionInputMode}
            open={permissionInputOpen}
            initialValues={companyPermissionInputValues}
            onFinish={onPermissionInputFinish}
            onCancel={onPermissionInputClose}
          />
        )}
      </Row>
      {contextHolder}
    </Col>
  );
}

export default PermissionsPage;
